import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  Download,
  ExternalLink,
  Mail,
  PartyPopper,
  Trophy,
} from 'lucide-react';

import { useSession } from '@/app/providers/session-provider';
import { usersQueries } from '@/entities/user';
import type { UserCertificate } from '@/entities/user/api/get-users-me-certificates';
import { useDownloadCertificate } from '@/shared/hooks/use-download-certificate';
import { resolveErrorMessage } from '@/shared/lib/errors';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';
import { cn } from '@/shared/utils';
import { useToast } from '@/shared/ui/toast-provider';
import { PageContainer } from '@/shared/ui/page-container';

const LICENSE_RENEWAL_URL = 'https://professions.dol.wa.gov/s/saw-help';

const formatDuration = (durationSecs?: number | null) => {
  if (!durationSecs || Number.isNaN(durationSecs)) {
    return '0 Minutes';
  }

  const hours = Math.floor(durationSecs / 3600);
  const minutes = Math.floor((durationSecs % 3600) / 60);

  if (hours >= 1) {
    if (minutes > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${hours} Hour${hours === 1 ? '' : 's'}`;
  }

  return `${minutes} Minute${minutes === 1 ? '' : 's'}`;
};

const formatDurationCompact = (durationSecs?: number | null) => {
  if (!durationSecs || Number.isNaN(durationSecs)) {
    return '0m';
  }

  const hours = Math.floor(durationSecs / 3600);
  const minutes = Math.floor((durationSecs % 3600) / 60);

  if (hours >= 1) {
    if (minutes > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${hours}h`;
  }

  return `${minutes}m`;
};

const formatIssueDate = (
  input?: string | null,
  options: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  },
) => {
  if (!input) {
    return '—';
  }

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString('en-US', options);
};

const slugify = (value?: string) => {
  if (!value) return 'certificate';
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 80)
      .replace(/^-+|-+$/g, '') || 'certificate'
  );
};

const buildCertificateFileName = (certificate?: UserCertificate) => {
  const courseSlug = slugify(certificate?.course?.title);

  const date = certificate?.created_at
    ? new Date(certificate.created_at)
    : null;
  const hasValidDate = date && !Number.isNaN(date.getTime());

  const dateSlug = hasValidDate ? date.toISOString().slice(0, 10) : 'issued';

  return `${courseSlug}-${dateSlug}.pdf`;
};

const getCourseDurationSeconds = (certificate?: UserCertificate | null) => {
  if (!certificate?.course) {
    return null;
  }

  const { duration_secs, duration_seconds } = certificate.course;

  if (typeof duration_secs === 'number') {
    return duration_secs;
  }

  if (typeof duration_seconds === 'number') {
    return duration_seconds;
  }

  return null;
};

export const CertificatesPage = () => {
  const router = useRouter();
  const { user } = useSession();
  const toast = useToast();

  const {
    data: certificatesData,
    isPending: isCertificatesLoading,
    isError: isCertificatesError,
    error: certificatesError,
  } = useQuery({
    ...usersQueries.certificates(),
    enabled: Boolean(user),
  });

  const certificates = certificatesData ?? [];

  const sortedCertificates = [...certificates].sort((a, b) => {
    const aTime = new Date(a.created_at ?? '').getTime();
    const bTime = new Date(b.created_at ?? '').getTime();
    if (Number.isNaN(aTime) && Number.isNaN(bTime)) return 0;
    if (Number.isNaN(aTime)) return 1;
    if (Number.isNaN(bTime)) return -1;
    return bTime - aTime;
  });

  const latestCertificate = sortedCertificates[0];

  const { download: downloadCertificate, isPending: isDownloadingCertificate } =
    useDownloadCertificate();

  const handleDownload = useCallback(
    async (certificateId: string) => {
      const certificate = sortedCertificates.find(
        (item) => item.id === certificateId,
      );

      const fileName = buildCertificateFileName(certificate);
      const shareTitle = certificate?.course?.title
        ? `${certificate.course.title} Certificate`
        : undefined;

      try {
        await downloadCertificate(certificateId, {
          fileName,
          shareTitle,
        });
      } catch (error) {
        toast({
          type: 'error',
          title: 'Download failed',
          description: resolveErrorMessage(
            error,
            'We could not download your certificate. Please try again.',
          ),
        });
      }
    },
    [downloadCertificate, sortedCertificates, toast],
  );

  const handleDownloadLatest = useCallback(async () => {
    if (!latestCertificate) {
      toast({
        type: 'info',
        title: 'No certificates yet',
        description: 'Complete your course to unlock your certificate.',
      });
      return;
    }

    await handleDownload(latestCertificate.id);
  }, [handleDownload, latestCertificate, toast]);

  const hasCertificates = sortedCertificates.length > 0;
  const latestCertificateDuration = getCourseDurationSeconds(latestCertificate);
  const completionHours = formatDurationCompact(latestCertificateDuration);
  const issuedOnSummary = formatIssueDate(latestCertificate?.created_at, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const firstName = user?.name ? user.name.split(' ')[0] : undefined;

  return (
    <main className="min-h-screen bg-slate-950 pb-24 text-white">
      <PageContainer maxWidth="mobile">
        <div className="flex flex-col gap-8 pt-8">
          <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="glass"
            size="icon"
            className="h-11 w-11 rounded-full border border-white/10 text-white shadow-[0_15px_40px_rgba(15,23,42,0.35)] transition hover:border-white/30 hover:bg-white/10"
            onClick={() => router.navigate({ to: '/courses' })}
            aria-label="Back to courses"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          {hasCertificates ? (
            <Badge className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-200">
              {sortedCertificates.length} Issued
            </Badge>
          ) : null}
        </div>

        <section className="overflow-hidden rounded-[32px] border border-white/10 bg-linear-to-br from-slate-900/90 via-slate-950/92 to-slate-950 shadow-[0_40px_110px_rgba(8,47,73,0.55)]">
          <div className="flex flex-col gap-10 p-7 sm:p-10 md:p-12">
            <div className="flex flex-col items-center gap-6 text-center sm:items-start sm:text-left">
              <div className="flex size-20 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-200 shadow-inner shadow-emerald-400/30">
                <PartyPopper className="h-10 w-10" aria-hidden />
              </div>

              <div className="flex flex-col gap-3">
                <h1 className="text-3xl font-semibold sm:text-4xl">
                  {firstName
                    ? `Congratulations, ${firstName}!`
                    : 'Congratulations!'}
                </h1>
                <p className="max-w-xl text-base leading-relaxed text-slate-300/90 sm:text-lg">
                  {hasCertificates
                    ? "You've successfully completed your continuing education course. Download and keep your certificates for your records."
                    : "You're one step away from unlocking your certificate. Finish your course to generate your official documentation."}
                </p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                <div className="flex items-center gap-4">
                  <span className="flex size-12 items-center justify-center rounded-2xl border border-emerald-500/20 bg-emerald-500/15 text-emerald-200">
                    <Trophy className="h-6 w-6" aria-hidden />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-200/80">
                      Course Completed
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-white">
                      {hasCertificates ? '100%' : 'In progress'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                <div className="flex items-center gap-4">
                  <span className="flex size-12 items-center justify-center rounded-2xl border border-sky-500/20 bg-sky-500/15 text-sky-200">
                    <Clock className="h-6 w-6" aria-hidden />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-200/80">
                      Hours Completed
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-white">
                      {completionHours}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="border-white/10 bg-white/5 text-white shadow-[0_18px_45px_rgba(8,47,73,0.45)] backdrop-blur">
              <CardHeader className="gap-3 p-6 sm:p-8">
                <CardTitle className="text-2xl font-semibold">
                  Your Certificates
                </CardTitle>
                <CardDescription className="text-base text-slate-200/80">
                  {hasCertificates
                    ? `Last updated ${issuedOnSummary}`
                    : 'Certificates will appear after course completion.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-5 border-t border-white/10 px-6 pb-6 pt-5 sm:px-8">
                <div className="flex flex-col gap-3 text-sm text-slate-200/85">
                  <div className="flex items-start gap-3">
                    <span className="mt-1 flex flex-shrink-0 size-8 items-center justify-center rounded-xl border border-white/10 bg-white/10">
                      <Mail className="h-4 w-4 text-emerald-200" aria-hidden />
                    </span>
                    <div className="space-y-1">
                      <p className="font-medium">
                        Digital copy sent to {user?.email ?? 'your email'}.
                      </p>
                      <p>
                        Keep this certificate handy for your Washington State
                        renewal.
                      </p>
                    </div>
                  </div>
                </div>
                {hasCertificates ? (
                  <div className="flex flex-col gap-4">
                    {sortedCertificates.map((certificate) => {
                      const durationLabel = formatDuration(
                        getCourseDurationSeconds(certificate),
                      );
                      const issuedDateLong = formatIssueDate(
                        certificate?.created_at,
                      );
                      const issuedDateShort = formatIssueDate(
                        certificate?.created_at,
                        {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        },
                      );

                      return (
                        <article
                          key={certificate.id}
                          className="rounded-2xl border border-white/10 bg-slate-900/40 p-5 sm:p-6"
                        >
                          <header className="flex flex-col gap-1">
                            <h2 className="text-lg font-semibold text-white">
                              {certificate.course?.title ??
                                'Course certificate'}
                            </h2>
                            <p className="text-sm text-slate-300/80">
                              Issued on {issuedDateLong}
                            </p>
                          </header>

                          <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-200/85">
                            <span className="inline-flex items-center gap-2">
                              <Clock className="h-4 w-4" aria-hidden />
                              {durationLabel}
                            </span>
                            <span className="inline-flex items-center gap-2">
                              <CalendarDays className="h-4 w-4" aria-hidden />
                              {issuedDateShort}
                            </span>
                          </div>

                          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <Button
                              type="button"
                              variant="secondary"
                              className="w-full gap-2 rounded-xl border-white/20 bg-white/10 text-white hover:bg-white/20 sm:w-auto"
                              onClick={() =>
                                void handleDownload(certificate.id)
                              }
                              disabled={isDownloadingCertificate}
                              aria-label={`Download ${
                                certificate.course?.title ?? 'certificate'
                              }`}
                            >
                              <Download className="h-4 w-4" aria-hidden />
                              Download
                            </Button>
                            <Badge className="self-start rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-emerald-200 sm:self-auto">
                              Certificate ID {certificate.id}
                            </Badge>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-white/20 bg-slate-950/30 p-6 text-center text-sm text-slate-300/80">
                    No certificates yet. Complete your course to generate your
                    first certificate.
                  </div>
                )}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <Button
                    type="button"
                    variant="glow"
                    className={cn(
                      'w-full sm:w-auto',
                      !hasCertificates && 'cursor-not-allowed opacity-60',
                    )}
                    onClick={() => void handleDownloadLatest()}
                    disabled={!hasCertificates || isDownloadingCertificate}
                  >
                    {isDownloadingCertificate ? (
                      <span className="relative flex items-center gap-2">
                        <span className="size-3 animate-ping rounded-full bg-white/70" />
                        Preparing download...
                      </span>
                    ) : (
                      <>
                        <Download className="h-5 w-5" aria-hidden />
                        Download certificate
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="glass"
                    className="w-full text-slate-200 transition hover:text-white sm:w-auto"
                    onClick={() => router.navigate({ to: '/courses' })}
                  >
                    Return to courses
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-white/5 text-white shadow-[0_18px_45px_rgba(8,47,73,0.35)] backdrop-blur">
              <CardHeader className="gap-2 p-6 sm:p-8">
                <CardTitle className="text-xl font-semibold">
                  Renew your license
                </CardTitle>
                <CardDescription className="text-sm text-slate-200/85">
                  Use your certificate when submitting your renewal application
                  on the WA DOL website.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4 border-t border-white/10 px-6 pb-6 pt-5 sm:px-8">
                <Button
                  asChild
                  variant="outline"
                  className="w-full justify-center rounded-2xl border-white/40 bg-white/10 text-white hover:bg-white/20"
                >
                  <a
                    href={LICENSE_RENEWAL_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ExternalLink className="h-4 w-4" aria-hidden />
                    Apply for license renewal
                  </a>
                </Button>
                <p className="text-xs text-slate-300/75">
                  The Department of Licensing may request additional proof of
                  course completion. Keep a backup copy stored securely.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
        </div>
      </PageContainer>
    </main>
  );
};
